import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trophy, Medal, Award, TrendingUp } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

interface LeaderboardUser {
  id: string;
  username: string;
  avatar_url: string;
  completedModules: number;
  totalTime: number;
  streak: number;
}

const DashboardLeaderboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);
  const [currentUserRank, setCurrentUserRank] = useState<number>(0);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Get all user progress
      const { data: allProgress } = await supabase
        .from("course_progress")
        .select("user_id, completed, completed_at");

      // Get all profiles
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, username, avatar_url");

      if (allProgress && profiles) {
        const userStats = profiles.map(profile => {
          const userProgress = allProgress.filter(p => p.user_id === profile.id);
          const completed = userProgress.filter(p => p.completed).length;
          const totalTime = completed * 45;
          const streak = calculateStreak(userProgress);

          return {
            id: profile.id,
            username: profile.username || "Anonymous",
            avatar_url: profile.avatar_url || "",
            completedModules: completed,
            totalTime,
            streak
          };
        }).sort((a, b) => b.completedModules - a.completedModules || b.totalTime - a.totalTime);

        setLeaderboard(userStats);
        const rank = userStats.findIndex(u => u.id === user.id) + 1;
        setCurrentUserRank(rank);
      }
    } catch (error) {
      console.error("Error loading leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStreak = (progress: any[]) => {
    const completedDates = progress
      .filter(p => p.completed_at)
      .map(p => new Date(p.completed_at).toDateString())
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    let streak = 0;
    for (let i = 0; i < completedDates.length; i++) {
      const expectedDate = new Date();
      expectedDate.setDate(expectedDate.getDate() - i);
      
      if (completedDates[i] === expectedDate.toDateString()) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return <Trophy className="h-6 w-6 text-yellow-500" />;
    if (index === 1) return <Medal className="h-6 w-6 text-gray-400" />;
    if (index === 2) return <Award className="h-6 w-6 text-amber-600" />;
    return <span className="text-lg font-bold text-muted-foreground">#{index + 1}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl font-bold">Leaderboard</h1>
          <p className="text-muted-foreground">See how you rank among fellow entrepreneurs</p>
        </div>

        {currentUserRank > 0 && (
          <Card className="border-primary">
            <CardContent className="flex items-center justify-between p-6">
              <div className="flex items-center gap-4">
                <TrendingUp className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Your Rank</p>
                  <p className="text-2xl font-bold">#{currentUserRank}</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-lg px-4 py-2">
                {leaderboard[currentUserRank - 1]?.completedModules || 0} modules
              </Badge>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Top Performers</CardTitle>
            <CardDescription>Based on completed modules and study time</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {leaderboard.map((user, index) => (
              <div
                key={user.id}
                className={`flex items-center justify-between p-4 rounded-lg transition-all hover:bg-accent ${
                  index < 3 ? 'bg-accent/50' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 flex justify-center">
                    {getRankIcon(index)}
                  </div>
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback>{user.username[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{user.username}</p>
                    <p className="text-sm text-muted-foreground">
                      {user.completedModules} modules • {Math.floor(user.totalTime / 60)}h {user.totalTime % 60}m
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant="outline">
                    🔥 {user.streak} day streak
                  </Badge>
                </div>
              </div>
            ))}

            {leaderboard.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No data yet. Start completing modules to appear on the leaderboard!
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardLeaderboard;
